/**
 * The way out of a screen.
 *
 * It lived inside `PageHeader`, which 35 screens use — and eight do not. Those
 * eight (Search, Preferences, Glossary, Notifications, the fleet map, the action
 * queue, Alerts, the locked-family page) had no way back on the page at all: a
 * reader who had drilled into one could leave only by the browser's own Back or
 * the rail, and the rail throws away where they came from. Lifting the control out
 * of the header gives every screen the same one from a single definition.
 *
 * It STEPS THE HISTORY rather than guessing a parent route, so it always returns
 * the reader to where they actually came from — which is the point of a back
 * control, and something a hardcoded parent link cannot do for a screen reachable
 * from four places.
 *
 * Deliberately not `useRouter()`: this renders outside a RouterProvider in the
 * screen tests, where that hook throws and takes the whole header down with it.
 * The browser history IS the router's history here, so reading it directly is both
 * equivalent and safe in every mounting context.
 */
import type { ReactNode } from "react";

/** Whether there is anywhere to go back TO. Hidden at the root and on the first
 *  entry of a session, so it is never a control that does nothing. */
export function canGoBack(): boolean {
  return (
    typeof window !== "undefined"
    && window.location.pathname !== "/"
    && window.history.length > 1
  );
}

export function BackButton({ className }: { className?: string }): ReactNode {
  if (!canGoBack()) return null;
  return (
    <button
      type="button"
      className={className}
      onClick={() => window.history.back()}
      style={backBtnStyle}
      aria-label="Go back to the previous screen"
      title="Back"
    >
      <span aria-hidden="true">←</span> Back
    </button>
  );
}

/* The control has to LOOK like one. It was 12px grey-on-transparent inside a
   hairline border — technically present, and read as decoration beside a page
   title twice its weight. A reader three levels into a site needs to see the way
   back, not hunt for it. */
const backBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 12px",
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--sk-color-ink-1)",
  background: "var(--sk-color-surface-1)",
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: 999,
  cursor: "pointer",
  flex: "0 0 auto",
  alignSelf: "center",
  boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
};
